import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Plus, Edit, Trash2, Eye, Star, TrendingUp, DollarSign, ShoppingBag, Users, ChevronRight, BarChart3, Truck, Send, Languages, Loader2, Zap, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AiVideoGenerator } from '../components/AiVideoGenerator';
import { VendorQuotesTab } from '../components/VendorQuotesTab';
import { VendorVouchersTab } from '../components/VendorVouchersTab';
import { VendorQuestionsTab } from '../components/VendorQuestionsTab';
import { AdminBoxtalShipDialog } from '../components/AdminBoxtalShipDialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const isProductFlashActive = (p) =>
  !!(p?.flash_sale_price && p?.flash_sale_end && new Date(p.flash_sale_end) > new Date() &&
     (!p.flash_sale_start || new Date(p.flash_sale_start) <= new Date()));

const isProductFlashScheduled = (p) =>
  !!(p?.flash_sale_price && p?.flash_sale_end && new Date(p.flash_sale_end) > new Date() &&
     p.flash_sale_start && new Date(p.flash_sale_start) > new Date());

export const VendorDashboard = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [stats, setStats] = useState(null);
  const [flashStats, setFlashStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [translatingId, setTranslatingId] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);
  const pollTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(pollTimerRef.current), []);
  const [orders, setOrders] = useState([]);
  const [boxtalOrder, setBoxtalOrder] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [shipData, setShipData] = useState({
    carrier: 'Colissimo',
    tracking_number: '',
    estimated_delivery: '3-5 jours ouvrés'
  });
  const [pendingQuestions, setPendingQuestions] = useState(0);

  const exportSalesCsv = async () => {
    try {
      const r = await axios.get(`${API}/vendors/me/export/sales`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : 'ventes.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Export CSV téléchargé' : 'CSV export downloaded');
    } catch {
      toast.error(language === 'fr' ? "Échec de l'export" : 'Export failed');
    }
  };

  useEffect(() => {
    axios.get(`${API}/vendors/me/questions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPendingQuestions((r.data || []).filter((q) => q.status === 'pending').length))
      .catch(() => {});
  }, [token]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const vendorRes = await axios.get(`${API}/vendors/me`, authHeaders);
        const [statsRes, productsRes, ordersRes, categoriesRes, flashStatsRes] = await Promise.all([
          axios.get(`${API}/vendors/me/stats`, authHeaders),
          axios.get(`${API}/products?vendor_id=${vendorRes.data.id}&status=active`, authHeaders).catch(() => ({ data: [] })),
          axios.get(`${API}/orders`, authHeaders),
          axios.get(`${API}/categories`),
          axios.get(`${API}/vendors/me/flash-stats`, authHeaders).catch(() => ({ data: null }))
        ]);

        setVendor(vendorRes.data);
        setStats(statsRes.data);
        setProducts(productsRes.data);
        setOrders(ordersRes.data);
        setCategories(categoriesRes.data);
        setFlashStats(flashStatsRes.data);
      } catch (error) {
        console.error('Error:', error);
        if (error.response?.status === 404) {
          navigate('/become-seller');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user.id, navigate]);

  const [newProduct, setNewProduct] = useState({
    name: '',
    name_fr: '',
    name_en: '',
    description: '',
    description_fr: '',
    description_en: '',
    price: '',
    compare_price: '',
    category_id: '',
    stock: '',
    sku: '',
    images: [''],
    flash_sale_price: '',
    flash_sale_end: '',
    flash_sale_start: ''
  });

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...newProduct,
        price: parseFloat(newProduct.price),
        compare_price: newProduct.compare_price ? parseFloat(newProduct.compare_price) : null,
        stock: parseInt(newProduct.stock),
        images: newProduct.images.filter(img => img.trim()),
        flash_sale_price: newProduct.flash_sale_price ? parseFloat(newProduct.flash_sale_price) : null,
        flash_sale_end: newProduct.flash_sale_end ? new Date(newProduct.flash_sale_end).toISOString() : null,
        flash_sale_start: newProduct.flash_sale_start ? new Date(newProduct.flash_sale_start).toISOString() : null
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, authHeaders);
        toast.success(language === 'fr' ? 'Produit mis à jour' : 'Product updated');
      } else {
        await axios.post(`${API}/products`, productData, authHeaders);
        toast.success(language === 'fr' ? 'Produit créé' : 'Product created');
      }

      // Refresh products
      const res = await axios.get(`${API}/products?vendor_id=${vendor.id}`, authHeaders);
      setProducts(res.data);
      setProductDialogOpen(false);
      resetProductForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const resetProductForm = () => {
    setNewProduct({
      name: '', name_fr: '', name_en: '',
      description: '', description_fr: '', description_en: '',
      price: '', compare_price: '', category_id: '', stock: '', sku: '', images: [''],
      flash_sale_price: '', flash_sale_end: '', flash_sale_start: ''
    });
    setEditingProduct(null);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer ce produit?' : 'Delete this product?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, authHeaders);
      setProducts(products.filter(p => p.id !== productId));
      toast.success(language === 'fr' ? 'Produit supprimé' : 'Product deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleTranslateProduct = async (productId) => {
    setTranslatingId(productId);
    try {
      const res = await axios.post(`${API}/products/${productId}/translate`, {}, authHeaders);
      toast.success(language === 'fr'
        ? `Fiche traduite en ${res.data.count} langues ✓`
        : `Listing translated into ${res.data.count} languages ✓`);
      setProducts((prev) => prev.map((p) => p.id === productId ? { ...p, ai_translated_at: new Date().toISOString() } : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de traduction' : 'Translation error'));
    } finally {
      setTranslatingId(null);
    }
  };

  const refreshVendorProducts = async () => {
    if (!vendor?.id) return;
    try {
      const res = await axios.get(`${API}/products?vendor_id=${vendor.id}&status=active`, authHeaders);
      setProducts(res.data);
    } catch { /* ignore */ }
  };

  const pollBulkStatus = async () => {
    try {
      const r = await axios.get(`${API}/products/translate-all/status`, authHeaders);
      setBulkJob(r.data);
      if (r.data?.status === 'running') {
        pollTimerRef.current = setTimeout(pollBulkStatus, 4000);
      } else if (r.data?.status === 'completed') {
        toast.success(language === 'fr'
          ? `Catalogue traduit : ${r.data.done} fiche(s) ✓${r.data.failed ? ` (${r.data.failed} échec(s))` : ''}`
          : `Catalog translated: ${r.data.done} listing(s) ✓${r.data.failed ? ` (${r.data.failed} failed)` : ''}`);
        refreshVendorProducts();
      }
    } catch { /* ignore */ }
  };

  const handleTranslateAll = async () => {
    try {
      const res = await axios.post(`${API}/products/translate-all`, {}, authHeaders);
      if (res.data.total === 0) {
        toast.info(language === 'fr' ? 'Toutes vos fiches sont déjà traduites ✓' : 'All your listings are already translated ✓');
        return;
      }
      toast.success(language === 'fr'
        ? `Traduction de ${res.data.total} fiche(s) lancée…`
        : `Translating ${res.data.total} listing(s)…`);
      setBulkJob({ status: 'running', total: res.data.total, done: 0 });
      pollTimerRef.current = setTimeout(pollBulkStatus, 4000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleShipOrder = async () => {
    if (!shipData.tracking_number) {
      toast.error(language === 'fr' ? 'Numéro de suivi requis' : 'Tracking number required');
      return;
    }
    
    try {
      await axios.post(`${API}/orders/${selectedOrder.id}/ship`, shipData, authHeaders);
      toast.success(language === 'fr' ? 'Commande expédiée! Email envoyé au client.' : 'Order shipped! Email sent to customer.');
      setOrders(orders.map(o => o.id === selectedOrder.id ? { ...o, status: 'shipped' } : o));
      setShipDialogOpen(false);
      setSelectedOrder(null);
      setShipData({ carrier: 'Colissimo', tracking_number: '', estimated_delivery: '3-5 jours ouvrés' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-8 w-64 mb-8" />
        <div className="grid grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const statCards = [
    { label: language === 'fr' ? 'Produits' : 'Products', value: stats?.total_products || 0, icon: Package, color: 'bg-blue-500' },
    { label: language === 'fr' ? 'Commandes' : 'Orders', value: stats?.total_orders || 0, icon: ShoppingBag, color: 'bg-green-500' },
    { label: language === 'fr' ? 'Revenus' : 'Revenue', value: `€${(stats?.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'bg-purple-500' },
    { label: language === 'fr' ? 'Note' : 'Rating', value: stats?.rating?.toFixed(1) || '0.0', icon: Star, color: 'bg-yellow-500' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vendor-dashboard">
      <div className="container-main py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('vendorDashboard')}</h1>
            <p className="text-muted-foreground mt-1">{vendor?.business_name}</p>
          </div>
          <Badge className={vendor?.status === 'active' ? 'badge-success' : 'badge-warning'}>
            {vendor?.status}
          </Badge>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <Card key={index} className="card-dashboard">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList>
            <TabsTrigger value="products">{t('myProducts')}</TabsTrigger>
            <TabsTrigger value="orders">{t('orders')}</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="vendor-quotes-tab-trigger">
              {language === 'fr' ? 'Devis B2B' : 'B2B Quotes'}
            </TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="vendor-vouchers-tab-trigger">
              {language === 'fr' ? 'Codes Promo' : 'Promo Codes'}
            </TabsTrigger>
            <TabsTrigger value="questions" data-testid="vendor-questions-tab-trigger">
              {language === 'fr' ? 'Questions' : 'Questions'}
              {pendingQuestions > 0 && (
                <span
                  className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center"
                  data-testid="questions-pending-badge"
                >
                  {pendingQuestions}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="vendor-analytics-tab" onClick={() => navigate('/vendor/analytics')}>
              {language === 'fr' ? 'Analytics' : 'Analytics'}
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                <CardTitle>{t('myProducts')}</CardTitle>
                <div className="flex items-center gap-2">
                  <AiVideoGenerator />
                  <Button
                    variant="outline"
                    onClick={handleTranslateAll}
                    disabled={bulkJob?.status === 'running'}
                    data-testid="translate-all-btn"
                  >
                    {bulkJob?.status === 'running' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {bulkJob.done ?? 0}/{bulkJob.total}
                      </>
                    ) : (
                      <>
                        <Languages className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Tout traduire (IA)' : 'Translate all (AI)'}
                      </>
                    )}
                  </Button>
                  <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" onClick={resetProductForm} data-testid="add-product-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('addProduct')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? t('editProduct') : t('addProduct')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleProductSubmit} className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t('productName')} (FR)</Label>
                          <Input
                            value={newProduct.name_fr}
                            onChange={(e) => setNewProduct({ ...newProduct, name_fr: e.target.value, name: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>{t('productName')} (EN)</Label>
                          <Input
                            value={newProduct.name_en}
                            onChange={(e) => setNewProduct({ ...newProduct, name_en: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>{t('description')} (FR)</Label>
                        <Textarea
                          value={newProduct.description_fr}
                          onChange={(e) => setNewProduct({ ...newProduct, description_fr: e.target.value, description: e.target.value })}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label>{t('description')} (EN)</Label>
                        <Textarea
                          value={newProduct.description_en}
                          onChange={(e) => setNewProduct({ ...newProduct, description_en: e.target.value })}
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>{t('productPrice')} (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newProduct.price}
                            onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>{language === 'fr' ? 'Prix barré' : 'Compare Price'} (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newProduct.compare_price}
                            onChange={(e) => setNewProduct({ ...newProduct, compare_price: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>{t('productStock')}</Label>
                          <Input
                            type="number"
                            value={newProduct.stock}
                            onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="border border-red-200 bg-red-50/50 rounded-lg p-3 space-y-3">
                        <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                          <Zap className="w-4 h-4" />
                          {language === 'fr' ? 'Vente Flash (optionnel)' : 'Flash Sale (optional)'}
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label>{language === 'fr' ? 'Prix flash (€)' : 'Flash price (€)'}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={newProduct.flash_sale_price}
                              onChange={(e) => setNewProduct({ ...newProduct, flash_sale_price: e.target.value })}
                              placeholder={language === 'fr' ? 'Ex : 149.99' : 'e.g. 149.99'}
                              data-testid="flash-sale-price-input"
                            />
                          </div>
                          <div>
                            <Label>{language === 'fr' ? 'Début (optionnel)' : 'Starts (optional)'}</Label>
                            <Input
                              type="datetime-local"
                              value={newProduct.flash_sale_start}
                              onChange={(e) => setNewProduct({ ...newProduct, flash_sale_start: e.target.value })}
                              data-testid="flash-sale-start-input"
                            />
                          </div>
                          <div>
                            <Label>{language === 'fr' ? 'Fin de la vente' : 'Sale ends'}</Label>
                            <Input
                              type="datetime-local"
                              value={newProduct.flash_sale_end}
                              onChange={(e) => setNewProduct({ ...newProduct, flash_sale_end: e.target.value })}
                              data-testid="flash-sale-end-input"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-red-700/70">
                          {language === 'fr'
                            ? 'Le prix flash doit être inférieur au prix normal. Un compte à rebours s\'affichera sur la fiche produit.'
                            : 'Flash price must be lower than the regular price. A countdown will show on the product page.'}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t('categories')}</Label>
                          <Select
                            value={newProduct.category_id}
                            onValueChange={(value) => setNewProduct({ ...newProduct, category_id: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  {getLocalizedName(cat)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>SKU</Label>
                          <Input
                            value={newProduct.sku}
                            onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>{language === 'fr' ? 'URL Image' : 'Image URL'}</Label>
                        <Input
                          value={newProduct.images[0]}
                          onChange={(e) => setNewProduct({ ...newProduct, images: [e.target.value] })}
                          placeholder="https://..."
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>
                          {t('cancel')}
                        </Button>
                        <Button type="submit" className="btn-primary">
                          {editingProduct ? t('save') : t('addProduct')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {flashStats?.units_sold > 0 && (
                  <div className="mb-6 border border-red-200 bg-red-50/50 rounded-xl p-4" data-testid="flash-stats-panel">
                    <p className="font-semibold text-red-700 flex items-center gap-1.5 mb-3">
                      <Zap className="w-4 h-4 fill-current" />
                      {language === 'fr' ? 'Performances Ventes Flash' : 'Flash Sales Performance'}
                    </p>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="bg-white rounded-lg p-3 border border-red-100">
                        <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Commandes' : 'Orders'}</p>
                        <p className="text-xl font-bold text-red-700" data-testid="flash-stats-orders">{flashStats.orders_count}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-red-100">
                        <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Unités vendues' : 'Units sold'}</p>
                        <p className="text-xl font-bold text-red-700" data-testid="flash-stats-units">{flashStats.units_sold}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-red-100">
                        <p className="text-xs text-muted-foreground">{language === 'fr' ? 'CA généré' : 'Revenue'}</p>
                        <p className="text-xl font-bold text-red-700" data-testid="flash-stats-revenue">€{flashStats.revenue.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {flashStats.products.map((fp) => (
                        <div key={fp.product_id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2 border border-red-100" data-testid={`flash-stats-product-${fp.product_id}`}>
                          <span className="font-medium truncate mr-3">{fp.name}</span>
                          <span className="text-muted-foreground whitespace-nowrap">
                            {fp.units} {language === 'fr' ? 'unité(s)' : 'unit(s)'} · <span className="font-semibold text-red-700">€{fp.revenue.toFixed(2)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {products.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Aucun produit' : 'No products yet'}
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'fr' ? 'Produit' : 'Product'}</TableHead>
                        <TableHead>{t('price')}</TableHead>
                        <TableHead>{t('productStock')}</TableHead>
                        <TableHead>{t('rating')}</TableHead>
                        <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <img
                                src={product.images?.[0] || 'https://via.placeholder.com/50'}
                                alt={getLocalizedName(product)}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                              <div>
                                <p className="font-medium">{getLocalizedName(product)}</p>
                                <p className="text-xs text-muted-foreground">SKU: {product.sku || 'N/A'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>€{product.price?.toFixed(2)}</TableCell>
                          <TableCell>{product.stock}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              {product.rating?.toFixed(1) || '0.0'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col items-start gap-1">
                              <Badge className={product.status === 'active' ? 'badge-success' : 'badge-warning'}>
                                {product.status}
                              </Badge>
                              {isProductFlashActive(product) && (
                                <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs" data-testid={`flash-sale-badge-row-${product.id}`}>
                                  <Zap className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'Vente flash' : 'Flash sale'}
                                </Badge>
                              )}
                              {isProductFlashScheduled(product) && (
                                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs" data-testid={`flash-scheduled-badge-${product.id}`}>
                                  <Zap className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'Flash programmée' : 'Flash scheduled'}
                                </Badge>
                              )}
                              {product.ai_translated_at ? (
                                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs" data-testid={`translated-badge-${product.id}`}>
                                  <Languages className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? '15 langues' : '15 languages'}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs" data-testid={`untranslated-badge-${product.id}`}>
                                  {language === 'fr' ? 'À traduire' : 'To translate'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                title={language === 'fr' ? 'Traduire en 15 langues (IA)' : 'Translate into 15 languages (AI)'}
                                onClick={() => handleTranslateProduct(product.id)}
                                disabled={translatingId === product.id}
                                data-testid={`translate-product-${product.id}`}
                              >
                                {translatingId === product.id
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : <Languages className="w-4 h-4" />}
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => navigate(`/product/${product.id}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                  setEditingProduct(product);
                                  setNewProduct({
                                    name: product.name,
                                    name_fr: product.name_fr,
                                    name_en: product.name_en,
                                    description: product.description,
                                    description_fr: product.description_fr,
                                    description_en: product.description_en,
                                    price: product.price.toString(),
                                    compare_price: product.compare_price?.toString() || '',
                                    category_id: product.category_id,
                                    stock: product.stock.toString(),
                                    sku: product.sku || '',
                                    images: product.images || [''],
                                    flash_sale_price: product.flash_sale_price?.toString() || '',
                                    flash_sale_end: toLocalInputValue(product.flash_sale_end),
                                    flash_sale_start: toLocalInputValue(product.flash_sale_start)
                                  });
                                  setProductDialogOpen(true);
                                }}
                                data-testid={`edit-product-${product.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => handleDeleteProduct(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <VendorQuotesTab />
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="vouchers">
            <VendorVouchersTab />
          </TabsContent>

          {/* Customer Questions Tab */}
          <TabsContent value="questions">
            <VendorQuestionsTab onPendingChange={setPendingQuestions} />
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                <CardTitle>{t('orders')}</CardTitle>
                <Button variant="outline" size="sm" onClick={exportSalesCsv} data-testid="export-sales-csv-btn">
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Export comptable (CSV)' : 'Accounting export (CSV)'}
                </Button>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Aucune commande' : 'No orders yet'}
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'fr' ? 'Commande' : 'Order'}</TableHead>
                        <TableHead>{t('orderDate')}</TableHead>
                        <TableHead>{language === 'fr' ? 'Articles' : 'Items'}</TableHead>
                        <TableHead>{t('total')}</TableHead>
                        <TableHead>{t('orderStatus')}</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-mono text-sm">#{order.id.slice(0, 8)}</TableCell>
                          <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>{order.items?.length || 0}</TableCell>
                          <TableCell>€{order.total?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={
                              order.status === 'delivered' ? 'badge-success' :
                              order.status === 'shipped' ? 'badge-info' :
                              order.status === 'cancelled' ? 'badge-error' : 'badge-warning'
                            }>
                              {order.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {(order.payment_status === 'paid' || order.payment_status === 'completed' || order.boxtal?.shipping_order_id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[#0F4C5C]"
                                  onClick={() => setBoxtalOrder(order)}
                                  data-testid={`vendor-boxtal-btn-${order.id}`}
                                >
                                  <Truck className="w-4 h-4 mr-1" />
                                  {order.boxtal?.shipping_order_id && order.boxtal?.status !== 'CANCELLED'
                                    ? (language === 'fr' ? 'Suivi Boxtal' : 'Boxtal tracking')
                                    : 'Boxtal'}
                                </Button>
                              )}
                              {order.status === 'pending' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600"
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setShipDialogOpen(true);
                                  }}
                                >
                                  <Truck className="w-4 h-4 mr-1" />
                                  {language === 'fr' ? 'Expédier' : 'Ship'}
                                </Button>
                              )}
                              {order.status === 'shipped' && (
                                <span className="text-sm text-muted-foreground flex items-center justify-end gap-1">
                                  <Send className="w-4 h-4" />
                                  {language === 'fr' ? 'Email envoyé' : 'Email sent'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Boxtal Ship Dialog (real carrier labels) */}
            <AdminBoxtalShipDialog
              order={boxtalOrder}
              open={!!boxtalOrder}
              onOpenChange={(o) => { if (!o) setBoxtalOrder(null); }}
              onUpdated={(orderId, patch) => setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o)))}
            />

            {/* Ship Order Dialog */}
            <Dialog open={shipDialogOpen} onOpenChange={setShipDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-[#0F4C5C]" />
                    {language === 'fr' ? 'Expédier la commande' : 'Ship Order'}
                  </DialogTitle>
                </DialogHeader>
                
                {selectedOrder && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Commande' : 'Order'}</p>
                      <p className="font-mono font-semibold">#{selectedOrder.id.slice(0, 8)}</p>
                      <p className="text-sm mt-1">{selectedOrder.items?.length || 0} {language === 'fr' ? 'articles' : 'items'} • €{selectedOrder.total?.toFixed(2)}</p>
                    </div>
                    
                    <div>
                      <Label>{language === 'fr' ? 'Transporteur' : 'Carrier'}</Label>
                      <Select
                        value={shipData.carrier}
                        onValueChange={(value) => setShipData({ ...shipData, carrier: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Colissimo">Colissimo</SelectItem>
                          <SelectItem value="Chronopost">Chronopost</SelectItem>
                          <SelectItem value="UPS">UPS</SelectItem>
                          <SelectItem value="DHL">DHL</SelectItem>
                          <SelectItem value="Mondial Relay">Mondial Relay</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>{language === 'fr' ? 'Numéro de suivi' : 'Tracking Number'} *</Label>
                      <Input
                        value={shipData.tracking_number}
                        onChange={(e) => setShipData({ ...shipData, tracking_number: e.target.value })}
                        placeholder="RM123456789FR"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>{language === 'fr' ? 'Délai de livraison estimé' : 'Estimated Delivery'}</Label>
                      <Input
                        value={shipData.estimated_delivery}
                        onChange={(e) => setShipData({ ...shipData, estimated_delivery: e.target.value })}
                        placeholder="3-5 jours ouvrés"
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>📧</strong> {language === 'fr' 
                          ? 'Un email de notification sera envoyé au client avec les informations de suivi.'
                          : 'A notification email will be sent to the customer with tracking information.'}
                      </p>
                    </div>
                    
                    <div className="flex gap-3 justify-end pt-2">
                      <Button variant="outline" onClick={() => setShipDialogOpen(false)}>
                        {language === 'fr' ? 'Annuler' : 'Cancel'}
                      </Button>
                      <Button onClick={handleShipOrder} className="btn-primary">
                        <Send className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Confirmer l\'expédition' : 'Confirm Shipment'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
